import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { IMarkerTypeAll } from '@core/models';
import { TranslateModule } from '@ngx-translate/core';
import { CdnUrlPipe } from '@shared/pipes';
import { LazyLoadImageModule } from 'ng-lazyload-image';

/** Component for displaying marker type categories as interactive cards */
@Component({
  selector: 'app-marker-types-card',
  imports: [LazyLoadImageModule, CdnUrlPipe, NgStyle, TranslateModule],
  templateUrl: './marker-types-card.html',
  styleUrl: './marker-types-card.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarkerTypesCard {
  private readonly router = inject(Router);

  /** Label text for the marker types section */
  readonly label = input<string>('');
  /** Array of marker type categories to display */
  readonly markerTypes = input.required<IMarkerTypeAll[]>();
  /** City identifier passed from parent component */
  readonly city = input<number>(0);
  /** Route identifier passed from parent component */
  readonly route = input<number>(0);

  /** Stores the identifier of the category the user is currently hovering over */
  private markerTypeHover!: number | null;

  private get omitCity(): boolean {
    const excludedSlugs = ['/weathers', '/routes'];
    return excludedSlugs.some((slug) => this.router.url.startsWith(slug));
  }

  /**
   * Changes styles for parent category labels when hovered
   * @param index - Identifier of the parent category
   * @returns Style object with background color, text color and border properties
   */
  changeStyle(index: number): any {
    const markerTypeSelected = this.markerTypes()[index];
    if (index === this.markerTypeHover) {
      return {
        'background-color': markerTypeSelected.color,
        color: '#FFFFFF',
        'border-color': markerTypeSelected.color,
      };
    } else {
      return { border: 'solid 2px' + markerTypeSelected.color };
    }
  }

  /**
   * Sets the hover state for a marker type category
   * @param event - Mouse event
   * @param index - Identifier of the parent category
   */
  setHoverToMarkerType(event: Event, index: number): void {
    if (event.type === 'mouseover') {
      this.markerTypeHover = index;
    } else {
      this.markerTypeHover = null;
    }
  }

  /**
   * Navigates to search page with appropriate filters based on selected marker
   * @param marker - The selected marker type category
   */
  goToSearch(marker: IMarkerTypeAll): void {
    const params: any = {
      marker_type: marker.id,
      routes: this.route(),
    };

    if (!this.omitCity) {
      params.city = this.city();
    }

    this.router.navigate([`search/`], { queryParams: params });
  }


  /** Navigates to search page with virtual reality filter and selected city/route */
  goToVr(): void {
    const params: any = { has_vr: 1, routes: this.route() };

    if (!this.omitCity) {
      params.city = this.city();
    }

    this.router.navigate([`search/`], { queryParams: params });
  }
}
